import React, { useState, useEffect, useRef } from "react";
import { Dayjs } from "dayjs";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Box } from "@mui/material";
import moment from "moment";

import CollapsibleTable from "./CollapsibleTable";
import { GET } from "../../services/HTTPServices";
import { alertMessage } from "../../services/alertService";
import { defaultPageSize, lang } from "../../constants";
import { ROUTES } from "../../constants/routes";

import {
  PageHeader,
  SearchAndActionBar,
  FilterSection,
  FilterRow,
  FilterLabel,
  TabsContainer,
  TabsList,
  Tab,
  DatePickerWrapper,
  DesktopSearchButton,
  MobileSearchButton,
  DesktopFilterSection,
  MobileFilterSection,
  TableContainer,
  EmptyState,
  EmptyStateText,
  OrderCount,
  AdvanceSearchButton,
  OrderCardsContainer,
  OrderCard,
  OrderCardHeader,
  OrderNumber,
  OrderTime,
  OrderCardBody,
  OrderInfoRow,
  OrderInfoLabel,
  OrderInfoValue,
  DeliveryBuddy,
  OrderPrice,
  DesktopTableContainer,
  ArrowRightIcon,
} from "../../styles/OrderHistory.styled";

import {
  SearchContainer,
  SearchIcon,
  SearchInput,
  SearchWrapper,
} from "../../styles/FoodPage.styles";
import searchSvg from "../../assets/svgs/FoodMenu/searchIcon.svg";
import ResponsivePagination from "../../components/pagination/ResponsivePagination";
import {
  LoadingOverlay,
  LoadingSpinner,
  LoadingSpinnerIcon,
} from "../../styles/loading";
import { LanguageText } from "../../styles";
import { PageTitle } from "../../styles/MenuManagement.styled";
import { DashboardContainer } from "../../styles/dashboard";
import { useSidebar } from "../../context/SidebarContext";

export const StyledAccessTimeIcon = styled(AccessTimeIcon)<{
  $activeTab?: string;
}>`
  && {
    color: ${(props) =>
      props.$activeTab === "pending" ? "#76B041" : "#6b7280"};
    font-size: 16px;
  }
`;

export const StyledPersonIcon = styled(PersonIcon)<{ $activeTab?: string }>`
  && {
    color: ${(props) =>
      props.$activeTab === "pending" ? "#76B041" : "#374151"};
    font-size: 16px;
  }
`;

export const StyledPaidOutlinedIcon = styled(PaidOutlinedIcon)<{
  $activeTab?: string;
}>`
  && {
    color: ${(props) =>
      props.$activeTab === "pending" ? "#76B041" : "#1f2937"};
    font-size: 16px;
  }
`;

export const StyledReceiptLongIcon = styled(ReceiptLongIcon)<{
  $activeTab?: string;
}>`
  && {
    color: ${(props) =>
      props.$activeTab === "pending" ? "#e39435" : "#6b7280"};
    font-size: 16px;
  }
`;

interface OrderHistoryState {
  search: string;
  orders: any[];
  pendingOrders: any[];
  status: string;
  from: Dayjs | null;
  to: Dayjs | null;
  fromFormatted: string | null;
  toFormatted: string | null;
  statusArray: { id: string; name: string }[];
  loading: boolean;
  openSearch: boolean;
  pageNo: number;
  totalItems: number;
  totalPageCount: number;
  refreshRows: boolean;
  activeTab: "pending" | "history";
}

const ProviderOrderHistory: React.FC = () => {
  const rows = useRef<any[]>([]);
  const PageSize = 5;
  const navigate = useNavigate();
  const { isSidebarCollapsed } = useSidebar();

  const [state, setState] = useState<OrderHistoryState>({
    search: "",
    orders: [],
    pendingOrders: [],
    status: "p",
    from: null,
    to: null,
    fromFormatted: null,
    toFormatted: null,
    statusArray: [
      { id: "", name: "Pending" },
      { id: "p", name: "Picking up order" },
      { id: "c", name: "Order collected" },
      { id: "g", name: "On the way you go" },
      { id: "d", name: "Completed" },
      { id: "r", name: "Reject" },
    ],
    loading: false,
    openSearch: false,
    pageNo: 1,
    totalItems: 0,
    totalPageCount: 100,
    refreshRows: false,
    activeTab: "pending",
  });

  useEffect(() => {
    getDataOrders(state.pageNo, state.status, false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const backTable = () => {
    getDataOrders(state.pageNo, state.status, false);
  };


  const valueChangedTo = (date: Dayjs | null) => {
    if (!date) {
      setState((prev) => ({
        ...prev,
        to: null,
        toFormatted: null,
      }));
      return;
    }

    var tzoffset = new Date().getTimezoneOffset() * 60000; //offset in milliseconds
    let selectedDate = new Date(date.valueOf() - tzoffset);
    let bDay: any = selectedDate.toISOString().slice(0, -1);

    setState((prev) => ({
      ...prev,
      to: date,
      toFormatted: bDay,
    }));
  };

  const valueChangedFrom = (date: Dayjs | null) => {
    if (!date) {
      setState((prev) => ({
        ...prev,
        from: null,
        fromFormatted: null,
      }));
      return;
    }

    var tzoffset = new Date().getTimezoneOffset() * 60000; //offset in milliseconds
    let selectedDate = new Date(date.valueOf() - tzoffset);
    let fromDate: any = selectedDate.toISOString().slice(0, -1);

    setState((prev) => ({
      ...prev,
      from: date,
      fromFormatted: fromDate,
    }));
  };

  const searchChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState((prev) => ({
      ...prev,
      search: e.target.value,
    }));
  };

  const keyPress = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setState((prev) => ({
        ...prev,
        refreshRows: false,
        loading: true,
      }));
      searchClicked(rows.current, (e.target as HTMLInputElement).value);
    }
  };

  const searchClicked = (rowsData: any[], search: string = state.search) => {
    if (search && search !== "") {
      const filteredOrders = rowsData.filter((row: any) =>
        row.orderNo.includes(search)
      );
      setState((prev) => ({
        ...prev,
        orders: filteredOrders,
        refreshRows: false, // First set to false
      }));

      setTimeout(() => {
        setState((prev) => ({
          ...prev,
          refreshRows: true,
          loading: false,
        }));
      }, 0);
    } else {
      setState((prev) => ({
        ...prev,
        orders: rowsData,
        refreshRows: false, // First set to false
      }));
      // Then immediately set to true to trigger re-render
      setTimeout(() => {
        setState((prev) => ({
          ...prev,
          refreshRows: true,
          loading: false,
        }));
      }, 0);
    }
  };

  const search = () => {
    setState((prev) => ({
      ...prev,
      refreshRows: false,
      loading: true,
      pageNo: 1,
    }));
    getDataOrders(1, state.status, true);
  };

  const pageClicked = async (page: number) => {
    setState((prev) => ({
      ...prev,
      pageNo: page,
    }));
    if (state.from && state.to) {
      getDataOrders(page, state.status, true);
    } else {
      getDataOrders(page, state.status);
    }
  };

  const getDataOrders = async (
    pageNo: number,
    status: any = state.status,
    isFilter: boolean = false
  ) => {
    setState((prev) => ({
      ...prev,
      loading: true,
      refreshRows: false,
    }));

    if (isFilter && (!state.from || !state.to)) {
      alertMessage("Warning!", "Please choose From and To!");
      setState((prev) => ({
        ...prev,
        loading: false,
        refreshRows: true,
      }));
    } else {
      const authUser = JSON.parse(localStorage.getItem("authUser") || "null");
      if (authUser && authUser.id) {
        try {
          const seg = status + "&fDate=";
          const url = isFilter
            ? `order/GetOrderBySPId?spId=${authUser.id.toString()}&oState=${seg}${
                state.fromFormatted
              }&tDate=${
                state.toFormatted
              }&pageNo=${pageNo}&pageSize=${PageSize}`
            : `order/GetOrderBySPId?spId=${authUser.id.toString()}&oState=${status}&pageNo=${pageNo}&pageSize=${PageSize}`;

          const res: any = await GET(url, "order");
          if (res) {
            rows.current = res.data.data;
            setState((prev) => ({
              ...prev,
              totalItems: res.data.totalItems,
              totalPageCount: res.data.totalPages,
            }));
            if (status === "d") searchClicked(res.data.data);
            else {
              setState((prev) => ({
                ...prev,
                pendingOrders: res.data.data,
                refreshRows: false, // First set to false
              }));

              setTimeout(() => {
                setState((prev) => ({
                  ...prev,
                  refreshRows: true,
                  loading: false,
                }));
              }, 0);
            }
          }
          setState((prev) => ({
            ...prev,
            loading: false,
            refreshRows: true,
          }));
        } catch {
          alertMessage("Error!", "Network Error!");
          setState((prev) => ({
            ...prev,
            loading: false,
            refreshRows: true,
          }));
        }
      } else {
        setState((prev) => ({
          ...prev,
          loading: false,
          refreshRows: true,
        }));
      }
    }
  };

  const toggleAdvanceSearch = () => {
    setState((prev) => ({
      ...prev,
      openSearch: !prev.openSearch,
    }));
  };

  const handleTabChange = (tab: "pending" | "history") => {
    let status = tab === "pending" ? "p" : "d";
    setState((prev) => ({
      ...prev,
      activeTab: tab,
      status: status,
      pageNo: 1,
    }));
    getDataOrders(1, status);
  };

  const handleViewDetails = (orderId: string) => {
    try {
      navigate(ROUTES.ORDER_DETAILS.replace(":id", orderId));
    } catch (error) {
      console.error("Navigation error:", error);
    }
  };

  const OrderCardComponent: React.FC<{ order: any }> = ({ order }) => (
    <OrderCard onClick={() => handleViewDetails(order.id)}>
      <div>
        <OrderCardHeader>
          <div>
            <OrderNumber
              $activeTab={state.activeTab === "history" ? "history" : "pending"}
              className="crop_text_cart_230"
            >
              Order No.
              {order.orderNo}
            </OrderNumber>
            <OrderTime $activeTab={state.activeTab}>
              <StyledAccessTimeIcon $activeTab={state.activeTab} />
              Order Time: {moment(order.orderDate).format("DD/MM/YYYY h:mm A")}
            </OrderTime>
          </div>
        </OrderCardHeader>

        <OrderCardBody>
          <OrderInfoRow>
            <OrderInfoLabel>
              <DeliveryBuddy $activeTab={state.activeTab}>
                <StyledPersonIcon $activeTab={state.activeTab} />
                Delivery Buddy:
              </DeliveryBuddy>
            </OrderInfoLabel>
            <OrderInfoValue>{order.courierName || ""}</OrderInfoValue>
          </OrderInfoRow>

          <OrderInfoRow>
            <OrderInfoLabel>
              <StyledPaidOutlinedIcon $activeTab={state.activeTab} />
              <OrderPrice $activeTab={state.activeTab}>
                ${order.total?.toFixed(2) || ""}
              </OrderPrice>
            </OrderInfoLabel>
            <OrderInfoValue className="ml-16">
              {state.activeTab === "history" && (
                <>
                  <StyledReceiptLongIcon $activeTab={state.activeTab} />
                  <span className="ml-8">Completed</span>
                </>
              )}
            </OrderInfoValue>
          </OrderInfoRow>
        </OrderCardBody>
      </div>
      <ArrowRightIcon />
    </OrderCard>
  );

  const currentLanguage = lang();
  const isEnglish =
    currentLanguage === "en" ||
    currentLanguage === "" ||
    currentLanguage === "en-US";

  return (
    <>
      {state.loading && (
        <LoadingOverlay>
          <LoadingSpinner>
            <LoadingSpinnerIcon />
          </LoadingSpinner>
        </LoadingOverlay>
      )}

          <DashboardContainer $isSidebarCollapsed={isSidebarCollapsed}>
        <PageHeader>
          <div>
            <PageTitle>{isEnglish ? "Orders" : "订单"}</PageTitle>
            <OrderCount>{state.orders.length} active orders found</OrderCount>
          </div>
        </PageHeader>

        <TabsContainer>
          <TabsList>
            <Tab
              $isActive={state.activeTab === "pending"}
              onClick={() => handleTabChange("pending")}
            >
              <LanguageText>Pending Orders</LanguageText>
            </Tab>
            <Tab
              $isActive={state.activeTab === "history"}
              onClick={() => handleTabChange("history")}
            >
              <LanguageText>Order History</LanguageText>
            </Tab>
          </TabsList>
        </TabsContainer>

        {state.activeTab === "history" && (
          <>
            <SearchAndActionBar>
              <SearchContainer className="mr-16">
                <SearchWrapper>
                  <SearchIcon>
                    <img src={searchSvg} alt="SearchIcon" />
                  </SearchIcon>
                  <SearchInput
                    id="search"
                    type="text"
                    placeholder="Search for past orders"
                    value={state.search}
                    onChange={searchChanged}
                    onKeyDown={keyPress}
                    height={48}
                  />
                </SearchWrapper>
              </SearchContainer>

              <AdvanceSearchButton
                onClick={toggleAdvanceSearch}
                className="mb-8"
              >
                {state.openSearch ? <RemoveIcon /> : <AddIcon />}
                <LanguageText>Date Search</LanguageText>
              </AdvanceSearchButton>
            </SearchAndActionBar>

            {state.openSearch && (
              <>
                <FilterSection>
                  <DesktopFilterSection>
                    <FilterRow>
                      <DatePickerWrapper>
                        <FilterLabel>
                          <LanguageText>From</LanguageText>
                        </FilterLabel>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label=""
                            value={state.from}
                            onChange={valueChangedFrom}
                            slotProps={{
                              textField: {
                                size: "small",
                                sx: {
                                  height: "40px",
                                  "& .MuiOutlinedInput-root": {
                                    height: "40px",
                                    borderRadius: "8px",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#e39435",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#e39435",
                                      },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#e39435",
                                      },
                                  },
                                  "& .MuiInputBase-input": {
                                    height: "40px",
                                    padding: "0 14px",
                                    boxSizing: "border-box",
                                  },
                                },
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </DatePickerWrapper>

                      <DatePickerWrapper>
                        <FilterLabel>
                          <LanguageText>To</LanguageText>
                        </FilterLabel>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label=""
                            value={state.to}
                            onChange={valueChangedTo}
                            slotProps={{
                              textField: {
                                size: "small",
                                sx: {
                                  height: "40px",
                                  "& .MuiOutlinedInput-root": {
                                    height: "40px",
                                    borderRadius: "8px",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#e39435",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#e39435",
                                      },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#e39435",
                                      },
                                  },
                                  "& .MuiInputBase-input": {
                                    height: "40px",
                                    padding: "0 14px",
                                    boxSizing: "border-box",
                                  },
                                },
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </DatePickerWrapper>

                      <DesktopSearchButton onClick={search}>
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 23 21"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8.54286 0C10.8086 0 12.9815 0.821783 14.5836 2.28457C16.1857 3.74735 17.0857 5.73131 17.0857 7.8C17.0857 9.732 16.3103 11.508 15.0354 12.876L15.3903 13.2H16.4286L23 19.2L21.0286 21L14.4571 15V14.052L14.1023 13.728C12.5521 14.9361 10.5809 15.5999 8.54286 15.6C6.27715 15.6 4.10424 14.7782 2.50214 13.3154C0.900048 11.8526 0 9.86869 0 7.8C0 5.73131 0.900048 3.74735 2.50214 2.28457C4.10424 0.821783 6.27715 0 8.54286 0ZM8.54286 2.4C5.25714 2.4 2.62857 4.8 2.62857 7.8C2.62857 10.8 5.25714 13.2 8.54286 13.2C11.8286 13.2 14.4571 10.8 14.4571 7.8C14.4571 4.8 11.8286 2.4 8.54286 2.4Z"
                            fill="white"
                          />
                        </svg>
                        <LanguageText>Search</LanguageText>
                      </DesktopSearchButton>
                    </FilterRow>
                  </DesktopFilterSection>

                  <MobileFilterSection>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        "@media (min-width: 768px)": {
                          display: "none",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <FilterLabel>
                          <LanguageText>From</LanguageText>
                        </FilterLabel>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="Choose"
                            value={state.from}
                            onChange={valueChangedFrom}
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                                sx: {
                                  height: "40px",
                                  "& .MuiOutlinedInput-root": {
                                    height: "40px",
                                    borderRadius: "8px",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#e39435",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#e39435",
                                      },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#e39435",
                                      },
                                  },
                                  "& .MuiInputBase-input": {
                                    height: "40px",
                                    padding: "0 14px",
                                    boxSizing: "border-box",
                                  },
                                },
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <FilterLabel>
                          <LanguageText>To</LanguageText>
                        </FilterLabel>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="Choose"
                            value={state.to}
                            onChange={valueChangedTo}
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                                sx: {
                                  height: "40px",
                                  "& .MuiOutlinedInput-root": {
                                    height: "40px",
                                    borderRadius: "8px",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "#e39435",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#e39435",
                                      },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#e39435",
                                      },
                                  },
                                  "& .MuiInputBase-input": {
                                    height: "40px",
                                    padding: "0 14px",
                                    boxSizing: "border-box",
                                  },
                                },
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Box>

                      <MobileSearchButton onClick={search}>
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 23 21"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8.54286 0C10.8086 0 12.9815 0.821783 14.5836 2.28457C16.1857 3.74735 17.0857 5.73131 17.0857 7.8C17.0857 9.732 16.3103 11.508 15.0354 12.876L15.3903 13.2H16.4286L23 19.2L21.0286 21L14.4571 15V14.052L14.1023 13.728C12.5521 14.9361 10.5809 15.5999 8.54286 15.6C6.27715 15.6 4.10424 14.7782 2.50214 13.3154C0.900048 11.8526 0 9.86869 0 7.8C0 5.73131 0.900048 3.74735 2.50214 2.28457C4.10424 0.821783 6.27715 0 8.54286 0ZM8.54286 2.4C5.25714 2.4 2.62857 4.8 2.62857 7.8C2.62857 10.8 5.25714 13.2 8.54286 13.2C11.8286 13.2 14.4571 10.8 14.4571 7.8C14.4571 4.8 11.8286 2.4 8.54286 2.4Z"
                            fill="white"
                          />
                        </svg>
                        <LanguageText>Search</LanguageText>
                      </MobileSearchButton>
                    </Box>
                  </MobileFilterSection>
                </FilterSection>
              </>
            )}
          </>
        )}

        {state.refreshRows &&
        state.orders &&
        (state.activeTab === "pending"
          ? state.pendingOrders.length > 0
          : state.orders.length > 0) ? (
          <>
            {state.activeTab === "pending" ? (
              <OrderCardsContainer>
                {state.pendingOrders.map((order: any, index: number) => (
                  <OrderCardComponent key={order.id || index} order={order} />
                ))}
              </OrderCardsContainer>
            ) : (
              <OrderCardsContainer>
                {state.orders.map((order: any, index: number) => (
                  <OrderCardComponent key={order.id || index} order={order} />
                ))}
              </OrderCardsContainer>
            )}

            <DesktopTableContainer>
              <TableContainer>
                <CollapsibleTable
                  rows={
                    state.activeTab === "pending"
                      ? state.pendingOrders
                      : state.orders
                  }
                  activeTab={state.activeTab}
                  callback={backTable}
                />
              </TableContainer>
            </DesktopTableContainer>
          </>
        ) : (
          <EmptyState>
            <EmptyStateText>
              <LanguageText>
                {state.activeTab === "pending"
                  ? "No pending orders."
                  : "No orders here."}
              </LanguageText>
            </EmptyStateText>
          </EmptyState>
        )}

        {state.refreshRows && state.orders && (
          <Box mt={4} display="flex" justifyContent="center">
            <ResponsivePagination
              currentPage={state.pageNo}
              totalPageCount={state.totalPageCount}
              pageSize={defaultPageSize}
              onPageChange={(page: any) => pageClicked(page)}
            />
          </Box>
        )}
      </DashboardContainer>
    </>
  );
};

export default ProviderOrderHistory;
